import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Button, Typography } from "@mui/material";
import { Drawer, Select, TextField, toast } from "@outpave/ds";
import { GET_CARD_PROGRAM_OPTIONS } from "graphql/cardProgramAdmin";
import { Stack } from "components/layout/reusableLayouts";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";

export interface CardProgramOption {
  platformIntegrationId: string;
  cardIssuer: string;
  productKey: string;
  productName: string;
  subAccountType?: string;
  connectionId?: string | null;
  requiresOnboarding: boolean;
  connected: boolean;
  cardIssuerAccountKeyField?: CardIssuerAccountKeyField | null;
}

interface CardIssuerAccountKeyField {
  labelKey: string;
  helpKey?: string | null;
}

interface AddCardProgramDrawerProps {
  open: boolean;
  onClose: () => void;
  onStart: (input: {
    platformIntegrationId: string;
    productKey: string;
    accountKey?: string;
  }) => Promise<StartCardProgramResult | undefined>;
}

interface StartCardProgramResult {
  hostedUrl?: string | null;
  connection?: { id: string; status: string } | null;
  program?: { id: string; status: string } | null;
}

const optionValue = (option: CardProgramOption) =>
  `${option.platformIntegrationId}:${option.productKey}`;

const AddCardProgramDrawer: React.FC<AddCardProgramDrawerProps> = ({
  open,
  onClose,
  onStart,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loading } = useQuery(GET_CARD_PROGRAM_OPTIONS, {
    fetchPolicy: "cache-and-network",
    skip: !open,
  });

  const [selected, setSelected] = useState("");
  const [accountKey, setAccountKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // A product already open on a connection is not something to open again.
  const options: CardProgramOption[] = useMemo(
    () =>
      (data?.getCardProgramOptions ?? []).filter(
        (option: CardProgramOption) => !option.connected,
      ),
    [data],
  );
  const chosen = options.find((option) => optionValue(option) === selected);
  const accountKeyField = chosen?.cardIssuerAccountKeyField;

  useEffect(() => {
    setSelected("");
    setAccountKey("");
  }, [open]);

  const { requestLeave, dialog } = useUnsavedChangesGuard(
    selected !== "" || accountKey.trim() !== "",
    onClose,
  );

  const handleClose = () => {
    if (submitting) return;
    requestLeave();
  };

  const handleSubmit = async () => {
    if (!chosen) return;
    setSubmitting(true);
    try {
      const result = await onStart({
        platformIntegrationId: chosen.platformIntegrationId,
        productKey: chosen.productKey,
        ...(accountKeyField && { accountKey: accountKey.trim() }),
      });
      // The issuer runs its own application page when it has one; otherwise
      // the connection still needs verifying here, and only a connection that
      // is already live leaves the program open right away.
      if (result?.hostedUrl) {
        window.open(result.hostedUrl, "_self");
        return;
      }
      if (result?.connection && result.connection.status !== "ACTIVE") {
        navigate(`/onboarding/issuer/${result.connection.id}`);
        return;
      }
      toast.success(t("CardProgramStartedToast"));
      onClose();
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : t("CardProgramStartError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={t("Add card program")}
        loading={loading || submitting}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={handleSubmit}
              disabled={
                submitting ||
                !chosen ||
                (!!accountKeyField && !accountKey.trim())
              }
            >
              {t("Continue")}
            </Button>
          </>
        }
      >
        <Stack gap="var(--space-4)">
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {t("AddCardProgramHelp")}
          </Typography>

          <Select
            label={t("Card program")}
            size="md"
            value={selected}
            onChange={(id) => setSelected(id == null ? "" : String(id))}
            fullWidth
            options={options.map((option) => ({
              id: optionValue(option),
              label: option.productName,
            }))}
          />

          {accountKeyField && (
            <TextField
              label={t(accountKeyField.labelKey)}
              helperText={
                accountKeyField.helpKey ? t(accountKeyField.helpKey) : undefined
              }
              size="md"
              value={accountKey}
              onChange={(e) => setAccountKey(e.target.value)}
              fullWidth
              required
              disabled={submitting}
            />
          )}

          {chosen?.requiresOnboarding && (
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {t("NewIssuerNeedsVerification")}
            </Typography>
          )}
        </Stack>
      </Drawer>
      {dialog}
    </>
  );
};

export default AddCardProgramDrawer;
